import { supabase } from "@/integrations/supabase/client";

export interface LeadData {
  form_type: "quote" | "contact" | "agent_application" | "branch_application" | "lead_magnet";
  name: string;
  email: string;
  phone?: string;
  insurance_type?: string;
  zip?: string;
  state?: string;
  message?: string;
  metadata?: Record<string, unknown>;
  recaptchaToken?: string;
}

export async function submitLead(data: LeadData): Promise<{ success: boolean; error?: string }> {
  try {
    // Insert into database
    const { error } = await supabase.from("leads").insert([{
      source: "website",
      form_type: data.form_type,
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      insurance_type: data.insurance_type || null,
      zip: data.zip || null,
      state: data.state || null,
      message: data.message || null,
      metadata: (data.metadata || {}) as unknown as Record<string, never>,
    }]);

    if (error) {
      console.error("Lead submission error:", error);
      return { success: false, error: error.message };
    }

    // Fire-and-forget notification
    supabase.functions.invoke("notify-lead", {
      body: {
        form_type: data.form_type,
        name: data.name,
        email: data.email,
        phone: data.phone,
        insurance_type: data.insurance_type,
        zip: data.zip,
        message: data.message,
      },
    }).catch(() => {}); // Don't block on notification failure

    return { success: true };
  } catch (err) {
    console.error("Lead submission failed:", err);
    return { success: false, error: "Submission failed. Please try again." };
  }
}

// reCAPTCHA v3 helper
let recaptchaLoaded = false;

export function loadRecaptcha(siteKey: string): Promise<void> {
  if (recaptchaLoaded) return Promise.resolve();
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.onload = () => {
      recaptchaLoaded = true;
      resolve();
    };
    document.head.appendChild(script);
  });
}

export async function getRecaptchaToken(siteKey: string, action: string): Promise<string | null> {
  try {
    await loadRecaptcha(siteKey);
    const token = await (window as any).grecaptcha.execute(siteKey, { action });
    return token;
  } catch {
    return null;
  }
}
